import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageListContainer, PageCreateButton } from "../../../components/Page";
import { useConfirm } from "../../../components/feedback";
import DataTableWithPagination from "../../../components/DataTable";
import ProjectDialog from "../../../components/GenericDialog";
import {
  ApplyToAllCustomersError,
  createAnomalyForAllCustomers,
} from "../../../lib/anomaly/applyToAllCustomers";
import { getAnomalyEventFormValidationError, mapAnomalyEventFormToPayload } from "../../../lib/anomaly/form";
import { anomalyGuideService } from "../../../services/anomalyGuideService";
import { anomalyService } from "../../../services/anomalyService";
import { canManageAnomalyGuides, getCurrentUserRoles, isAdmin } from "../../../utils/authorization";
import AnomalyModel from "../../anomalies/model";
import AnomalyEventDialogBody from "../../anomalies/ui/components/AnomalyEventDialogBody";
import AnomalyGuideModel, { getAnomalyGuideCols } from "../model";
import CreateAnomalyGuideDialogBody from "./components/CreateAnomalyGuideDialogBody";
import EditAnomalyGuideDialogBody from "./components/EditAnomalyGuideDialogBody";
import ViewAnomalyGuideDialogBody from "./components/ViewAnomalyGuideDialogBody";

const mapFormToPayload = (formJson: Record<string, string>) => ({
  category: formJson.category?.trim() || "",
  subCategory: formJson.subCategory?.trim() || "",
  classificationGuidance: formJson.classificationGuidance?.trim() || "",
});

const ANOMALY_GUIDE_SEARCH_FIELDS = ["category", "subCategory", "classificationGuidance"];

function AnomalyGuidesPage() {
  const { t } = useTranslation();
  const datatable = useRef<any>(null);
  const createDialogRef = useRef<any>(null);
  const editDialogRef = useRef<any>(null);
  const viewDialogRef = useRef<any>(null);
  const createAnomalyDialogRef = useRef<any>(null);
  const viewedGuideRef = useRef<Record<string, unknown> | null>(null);
  const confirm = useConfirm();
  const [canManageGuides, setCanManageGuides] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);

  const guideCols = useMemo(() => getAnomalyGuideCols(t), [t]);

  useEffect(() => {
    void getCurrentUserRoles().then((roles) => {
      setCanManageGuides(canManageAnomalyGuides(roles));
      setIsAdminUser(isAdmin(roles));
    });
  }, []);

  useEffect(() => {
    const handleGlobalRefresh = () => {
      datatable.current?.refresh?.();
    };

    window.addEventListener("refreshDataTable", handleGlobalRefresh);
    return () => window.removeEventListener("refreshDataTable", handleGlobalRefresh);
  }, []);

  const showSnack = (opts: unknown) => {
    window.dispatchEvent(new CustomEvent("showGlobalNotification", { detail: opts }));
  };

  const openCreateDialog = () => {
    createDialogRef.current?.showDialog(AnomalyGuideModel.createDialogOpts(t));
  };

  const createSubmitAction = (formJson: Record<string, string>) => {
    anomalyGuideService
      .createAnomalyGuide(mapFormToPayload(formJson))
      .then(() =>
        AnomalyGuideModel.message(
          AnomalyGuideModel.SUCCESS,
          t("common.createRequest"),
          t("anomalyGuides.createdSuccess"),
          Date.now(),
        ),
      )
      .then((opts) => {
        showSnack(opts);
        setTimeout(() => datatable.current?.refresh?.(), 500);
      })
      .catch((error: unknown) => {
        console.error(error);
      });
  };

  const editSubmitAction = (formJson: Record<string, string>) => {
    anomalyGuideService
      .updateAnomalyGuide({
        id: formJson.id,
        ...mapFormToPayload(formJson),
      })
      .then(() =>
        AnomalyGuideModel.message(
          AnomalyGuideModel.SUCCESS,
          t("common.updateRequest"),
          t("anomalyGuides.updatedSuccess"),
          Date.now(),
        ),
      )
      .then((opts) => {
        showSnack(opts);
        setTimeout(() => datatable.current?.refresh?.(), 500);
      })
      .catch((error: unknown) => {
        console.error(error);
      });
  };

  const handleDelete = async (item: { id?: string; category?: string; subCategory?: string }) => {
    return new Promise((resolve) => {
      confirm({
        description: t("anomalyGuides.deleteConfirm", {
          id: `${item.category || ""} / ${item.subCategory || item.id}`,
        }),
      })
        .then(() => anomalyGuideService.deleteAnomalyGuide(String(item.id)))
        .then(() => resolve({}))
        .then(() =>
          AnomalyGuideModel.message(
            AnomalyGuideModel.SUCCESS,
            t("common.deleteRequest"),
            t("anomalyGuides.deletedSuccess"),
            Date.now(),
          ),
        )
        .then((opts) => {
          showSnack(opts);
          setTimeout(() => datatable.current?.refresh?.(), 500);
        })
        .catch((error: unknown) => {
          console.error(error);
        });
    });
  };

  const handleEdit = async (item: Record<string, unknown>) => {
    editDialogRef.current?.showDialog(AnomalyGuideModel.editDialogOpts(t), item);
  };

  const handleView = async (item: Record<string, unknown>) => {
    viewedGuideRef.current = item;
    viewDialogRef.current?.showDialog(AnomalyGuideModel.viewDialogOpts(t), item);
  };

  const openCreateAnomalyFromGuide = (formJson: Record<string, string>) => {
    const guide = viewedGuideRef.current ?? {};
    const category = String(guide.category ?? formJson.category ?? "").trim();
    const subCategory = String(guide.subCategory ?? formJson.subCategory ?? "").trim();
    const classificationGuidance = String(
      guide.classificationGuidance ?? formJson.classificationGuidance ?? "",
    ).trim();

    window.setTimeout(() => {
      createAnomalyDialogRef.current?.showDialog(AnomalyModel.createDialogOpts(t), {
        category,
        subCategory,
        classificationGuidance,
        fromAnomalyGuide: true,
      });
    }, 0);
  };

  const showValidationError = (message: string) => {
    AnomalyModel.message(AnomalyModel.ERROR, t("common.validationError"), message, Date.now()).then((opts) =>
      showSnack(opts),
    );
  };

  const createAnomalySubmitAction = async (formJson: Record<string, string>) => {
    const applyToAllCustomers = formJson.applyToAllCustomers === "true";
    const validationError = getAnomalyEventFormValidationError(formJson, {
      requireCustomer: isAdminUser && !applyToAllCustomers,
      allowMissingWorkHours: applyToAllCustomers,
      customer: {
        startWorkTime: formJson.customerStartWorkTime,
        endWorkTime: formJson.customerEndWorkTime,
        timeZone: formJson.customerTimeZone,
      },
    });
    if (validationError) {
      showValidationError(validationError);
      return false;
    }

    if (applyToAllCustomers) {
      try {
        const { created, skipped } = await createAnomalyForAllCustomers(formJson);
        if (created === 0 && skipped === 0) {
          showValidationError(t("anomalies.noCustomersToApply"));
          return false;
        }
        const opts = await AnomalyModel.message(
          AnomalyModel.SUCCESS,
          t("common.createRequest"),
          t("anomalies.createdForAllCustomers", { created, skipped }),
          Date.now(),
        );
        showSnack(opts);
      } catch (error) {
        console.error(error);
        const created = error instanceof ApplyToAllCustomersError ? error.created : 0;
        const skipped = error instanceof ApplyToAllCustomersError ? error.skipped : 0;
        const opts = await AnomalyModel.message(
          AnomalyModel.ERROR,
          t("common.createRequest"),
          t("anomalies.applyToAllFailed", { created, skipped }),
          Date.now(),
        );
        showSnack(opts);
        return false;
      }
      return undefined;
    }

    anomalyService
      .createAnomalyEvent(mapAnomalyEventFormToPayload(formJson))
      .then(() =>
        AnomalyModel.message(
          AnomalyModel.SUCCESS,
          t("common.createRequest"),
          t("anomalies.createdSuccess"),
          Date.now(),
        ),
      )
      .then((opts) => {
        showSnack(opts);
      })
      .catch((error: unknown) => {
        console.error(error);
      });

    return undefined;
  };

  return (
    <PageListContainer>
      <ProjectDialog
        ref={createDialogRef}
        submitAction={createSubmitAction}
        body={CreateAnomalyGuideDialogBody}
        maxWidth="md"
        fullWidth
      />
      <ProjectDialog
        ref={editDialogRef}
        submitAction={editSubmitAction}
        body={EditAnomalyGuideDialogBody}
        maxWidth="md"
        fullWidth
      />
      <ProjectDialog
        ref={viewDialogRef}
        submitAction={openCreateAnomalyFromGuide}
        body={ViewAnomalyGuideDialogBody}
        maxWidth="md"
        fullWidth
      />
      <ProjectDialog
        ref={createAnomalyDialogRef}
        submitAction={createAnomalySubmitAction}
        body={AnomalyEventDialogBody}
        maxWidth="md"
        fullWidth
      />

      <DataTableWithPagination
        ref={datatable}
        cols={guideCols}
        handleView={handleView}
        handleEdit={canManageGuides ? handleEdit : undefined}
        handleDelete={canManageGuides ? handleDelete : undefined}
        headerActions={
          canManageGuides ? (
            <PageCreateButton onClick={openCreateDialog}>{t("anomalyGuides.newGuide")}</PageCreateButton>
          ) : undefined
        }
        fetchUrl="/anomaly-guide/read"
        searchKey="category"
        searchFields={ANOMALY_GUIDE_SEARCH_FIELDS}
        searchPlaceholder={t("anomalyGuides.searchPlaceholder")}
        searchInfo={t("anomalyGuides.searchInfo")}
      />
    </PageListContainer>
  );
}

export default AnomalyGuidesPage;
